/**
 * Composer-assets hooks — signatures, templates, snippets (Vault-backed).
 * Account-scoped CRUD over the /arches/email composer routes.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import type { MailboxSignatureDto, MailboxTemplateDto, MailboxSnippetDto } from '@/lib/api';

const mb = queryKeys.email.mailbox;

// ── Signatures ──
export function useEmailSignatures(accountId: string | undefined) {
  return useQuery<MailboxSignatureDto[]>({
    queryKey: mb.signatures(accountId ?? ''),
    queryFn: async () => (await api.listEmailMailboxSignatures(accountId as string)).signatures,
    enabled: !!accountId,
  });
}

export function useCreateSignature(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; bodyHtml: string; isDefault?: boolean }) =>
      api.createEmailMailboxSignature({ accountId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.signatures(accountId) }),
  });
}

export function useUpdateSignature(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      signatureId,
      ...input
    }: {
      signatureId: string;
      name?: string;
      bodyHtml?: string;
      isDefault?: boolean;
    }) => api.updateEmailMailboxSignature(signatureId, { accountId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.signatures(accountId) }),
  });
}

export function useDeleteSignature(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (signatureId: string) => api.deleteEmailMailboxSignature(signatureId, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.signatures(accountId) }),
  });
}

// ── Templates ──
export function useEmailTemplates(accountId: string | undefined) {
  return useQuery<MailboxTemplateDto[]>({
    queryKey: mb.templates(accountId ?? ''),
    queryFn: async () => (await api.listEmailMailboxTemplates(accountId as string)).templates,
    enabled: !!accountId,
  });
}

export function useCreateTemplate(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      subject?: string | null;
      bodyHtml: string;
      category?: string | null;
    }) => api.createEmailMailboxTemplate({ accountId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.templates(accountId) }),
  });
}

export function useDeleteTemplate(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (templateId: string) => api.deleteEmailMailboxTemplate(templateId, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.templates(accountId) }),
  });
}

// ── Snippets ──
export function useEmailSnippets(accountId: string | undefined) {
  return useQuery<MailboxSnippetDto[]>({
    queryKey: mb.snippets(accountId ?? ''),
    queryFn: async () => (await api.listEmailMailboxSnippets(accountId as string)).snippets,
    enabled: !!accountId,
  });
}

export function useCreateSnippet(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { trigger: string; body: string }) =>
      api.createEmailMailboxSnippet({ accountId, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.snippets(accountId) }),
  });
}

export function useDeleteSnippet(accountId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (snippetId: string) => api.deleteEmailMailboxSnippet(snippetId, accountId),
    onSuccess: () => qc.invalidateQueries({ queryKey: mb.snippets(accountId) }),
  });
}
